import React from "react";
import { Helmet } from "react-helmet";
import { useLocation, useParams } from "react-router-dom";
import {
    FaLinkedin,
    FaXTwitter,
    FaFacebook,
    FaInstagram,
    FaYoutube,
} from "react-icons/fa6";

export default function AuthorProfile() {
    const { state } = useLocation();
    const { slug } = useParams();

    // 🔥 ONLY IMAGE FROM API (SAFE)
    const authorImage =
        state?.user?.image ||
        "https://image.blogysports.com/WhatsApp%20Image%202025-11-21%20at%2011.08.41.jpeg";

    /* ================= HARDCODED CONTENT ================= */
    const authorName = "Naveen Kumar";
    const authorRole = "Author & Sports Analyst";

    const authorBio =
        "Naveen Kumar is a tech blogger and sports enthusiast who writes about technology, AI, software tutorials, and the latest sports updates. He focuses on creating simple, original, and reader-friendly content that delivers real value to his audience.";

    const socialLinks = {
        twitter: "https://x.com/blogysports",
        linkedin: "https://linkedin.com/company/blogysports",
        facebook: "https://facebook.com/blogysports",
        instagram: "https://instagram.com/blogysports",
        youtube: "https://youtube.com/@pythoncodebynaveen",
    };

    return (
        <>
            {/* ================= SEO ================= */}
            <Helmet>
                <title>{authorName} – Author | BlogySports</title>
                <meta name="description" content={authorBio} />
                <link
                    rel="canonical"
                    href={`https://blogysports.com/author/${slug}`}
                />
            </Helmet>

            {/* ================= HERO ================= */}
            <section className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white">
                <div className="max-w-7xl mx-auto px-6 py-16 flex flex-col md:flex-row items-center gap-10">
                    <img
                        src={authorImage}
                        alt={authorName}
                        className="w-40 h-40 rounded-2xl border-4 border-white shadow-xl object-cover"
                    />

                    <div>
                        <h1 className="text-4xl font-extrabold">
                            {authorName}
                        </h1>

                        <p className="mt-2 text-lg opacity-90">
                            {authorRole}
                        </p>

                        <p className="mt-4 max-w-2xl text-white/90 leading-relaxed">
                            {authorBio}
                        </p>
                    </div>
                </div>
            </section>

            {/* ================= MAIN ================= */}
            <main className="max-w-7xl mx-auto px-6 py-14">
                {/* ABOUT */}
                <div className="bg-white rounded-2xl shadow-md p-8 border border-red-100">
                    <h2 className="text-2xl font-bold mb-4">
                        About the Author
                    </h2>

                    <p className="text-gray-700 leading-relaxed">
                        {authorBio}
                    </p>
                </div>

                {/* EXPERTISE */}
                <div className="mt-12">
                    <h2 className="text-2xl font-bold mb-6">
                        Areas of Expertise
                    </h2>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {[
                            "Cricket",
                            "Football",
                            "Tennis",
                            "Basketball",
                            "Match Analysis",
                            "Live Coverage",
                            "Transfers",
                            "World Sports",
                        ].map((skill) => (
                            <div
                                key={skill}
                                className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center font-semibold text-red-700"
                            >
                                {skill}
                            </div>
                        ))}
                    </div>
                </div>

                {/* SOCIAL LINKS */}
                <div className="mt-14">
                    <h2 className="text-2xl font-bold mb-6">
                        Connect with {authorName}
                    </h2>

                    <div className="flex flex-wrap gap-4">
                        <SocialButton
                            href={socialLinks.linkedin}
                            icon={FaLinkedin}
                            label="LinkedIn"
                            color="linkedin"
                        />
                        <SocialButton
                            href={socialLinks.twitter}
                            icon={FaXTwitter}
                            label="X (Twitter)"
                            color="twitter"
                        />
                        <SocialButton
                            href={socialLinks.facebook}
                            icon={FaFacebook}
                            label="Facebook"
                            color="facebook"
                        />
                        <SocialButton
                            href={socialLinks.instagram}
                            icon={FaInstagram}
                            label="Instagram"
                            color="instagram"
                        />
                        <SocialButton
                            href={socialLinks.youtube}
                            icon={FaYoutube}
                            label="YouTube"
                            color="youtube"
                        />
                    </div>
                </div>
            </main>
        </>
    );
}

/* =====================================================
   SOCIAL BUTTON
   ===================================================== */
function SocialButton({ href, icon: Icon, label, color }) {
    const COLORS = {
        linkedin: "border-blue-600 bg-blue-50 hover:bg-blue-100 text-blue-600",
        twitter: "border-black bg-gray-100 hover:bg-gray-200 text-black",
        facebook: "border-blue-700 bg-blue-50 hover:bg-blue-100 text-blue-700",
        instagram: "border-pink-500 bg-pink-50 hover:bg-pink-100 text-pink-500",
        youtube: "border-red-600 bg-red-50 hover:bg-red-100 text-red-600",
    };

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-4 px-6 py-3 rounded-xl border transition hover:shadow-md ${COLORS[color]}`}
        >
            <Icon className="text-2xl" />
            <span className="font-semibold">{label}</span>
        </a>
    );
}
